import type { Lang } from "../lib/prefs";
import { useSites } from "../lib/sites";
import { useMetrics } from "../lib/metrics";
import type { EngineRow, Metrics, RunProgress } from "../lib/metrics";
import { useRecommendations, downloadBundle } from "../lib/recommendations";
import type { Recommendation } from "../lib/recommendations";
import { useVerifications } from "../lib/verifications";
import type { Verification } from "../lib/verifications";
import type { RecStatus } from "../lib/database.types";

// M4 — the visibility dashboard, computed from REAL runs (mock data dropped).
// KPI tiles keep the two metrics SEPARATE: % recomendada (direct queries) and
// posición al enlistar (ranking queries). Trend chart shows % recommended over
// weeks (up = better). Empty state offers "Medir ahora".

const T = {
  es: {
    title: "Visibilidad",
    k1: "% recomendada · 4 intentos",
    k1n: "te recomienda al preguntar directo",
    k2: "Posición al enlistar",
    k2n: "lugar cuando aparece",
    k3: "Recomendada al 1.er intento",
    k3n: "sin repreguntar",
    k4: "Cuota de voz en IA",
    k4n: "vs. competidores citados",
    chT: "% recomendada por la IA",
    chS: "por semana · más arriba = mejor",
    aiT: "Motores de IA",
    week: "última medición",
    thP: "Perfil", thQ: "Consulta", thTry: "Intento", thR: "Rank", thCite: "Cita",
    miss: "Miss", none: "ninguna", link: "con enlace", ment: "mención",
    emptyT: "Aún no hay mediciones",
    emptyP: "Ejecuta la primera medición para ver qué tan seguido la IA te recomienda a tus clientes ideales. Toma un momento.",
    onbT: "Analizando tu sitio y preparando tu primer análisis…",
    onbP: "Estamos leyendo tu sitio, creando tu perfil de cliente ideal y midiendo tu visibilidad en la IA. Puedes editar tus perfiles y consultas cuando quieras.",
    measure: "Medir ahora", measuring: "Midiendo… esto tarda un momento",
    runErr: "No se pudo completar la medición. Intenta de nuevo.",
    noSite: "Selecciona un sitio para ver su panel.",
    subRuns: "corridas", subQ: "consultas", subP: "perfiles",
    search: "búsqueda en vivo", base: "sin búsqueda",
    sugT: "Sugerencias de la semana", sugS: "acepta o rechaza",
    accept: "Aceptar", reject: "Rechazar", download: "Descargar",
    impHi: "impacto alto", impMd: "impacto medio", impLo: "impacto bajo",
    stPend: "pendiente · por verificar", stDone: "verificada · en vivo", stRej: "rechazada",
    fnote: "Aceptar deja la sugerencia pendiente. Verificar recorre tu sitio y confirma el cambio.",
    genBtn: "✨ Generar sugerencias", genLoad: "Generando sugerencias…",
    emptyRecs: "Aún no hay sugerencias. Genera las de esta semana a partir de tus brechas de visibilidad.",
    recsErr: "No se pudieron cargar las sugerencias.",
    files: "archivos",
    verify: "Verificar", verifying: "Verificando…",
    verFailNote: "Aún no se detecta el cambio en vivo. Publícalo y vuelve a verificar.",
    verT: "Cambios verificados", verS: "se confirmó que ocurrieron",
    verEmpty: "Aún no hay cambios verificados. Verifica una sugerencia pendiente para recorrer tu sitio.",
    verErr: "No se pudieron cargar las verificaciones.",
    ckPage: "Renderiza sin JS · 200", ckAnswer: "Bloque de respuesta directa presente",
    ckSchema: "Schema", ckFail: "no detectado",
  },
  en: {
    title: "Visibility",
    k1: "% recommended · 4 tries",
    k1n: "recommends you on a direct ask",
    k2: "Rank when listed",
    k2n: "place when it appears",
    k3: "Recommended on 1st try",
    k3n: "without re-asking",
    k4: "AI share of voice",
    k4n: "vs. cited competitors",
    chT: "% recommended by AI",
    chS: "per week · higher = better",
    aiT: "AI engines",
    week: "latest measurement",
    thP: "Persona", thQ: "Query", thTry: "Try", thR: "Rank", thCite: "Citation",
    miss: "Miss", none: "none", link: "linked", ment: "mention",
    emptyT: "No measurements yet",
    emptyP: "Run the first measurement to see how often AI recommends you to your ideal customers. It takes a moment.",
    onbT: "Analyzing your site and preparing your first analysis…",
    onbP: "We're reading your site, building your ideal-customer persona, and measuring your AI visibility. You can edit your personas and queries anytime.",
    measure: "Measure now", measuring: "Measuring… this takes a moment",
    runErr: "The measurement could not complete. Please try again.",
    noSite: "Select a site to see its dashboard.",
    subRuns: "runs", subQ: "queries", subP: "personas",
    search: "live search", base: "no search",
    sugT: "This week's suggestions", sugS: "accept or reject",
    accept: "Accept", reject: "Reject", download: "Download",
    impHi: "high impact", impMd: "medium impact", impLo: "low impact",
    stPend: "pending · to verify", stDone: "verified · live", stRej: "rejected",
    fnote: "Accepting leaves a suggestion pending. Verify re-crawls your site to confirm the change.",
    genBtn: "✨ Generate suggestions", genLoad: "Generating suggestions…",
    emptyRecs: "No suggestions yet. Generate this week's from your visibility gaps.",
    recsErr: "Could not load suggestions.",
    files: "files",
    verify: "Verify", verifying: "Verifying…",
    verFailNote: "The change isn't live yet. Publish it and verify again.",
    verT: "Verified changes", verS: "confirmed they happened",
    verEmpty: "No verified changes yet. Verify a pending suggestion to re-crawl your site.",
    verErr: "Could not load verifications.",
    ckPage: "Renders without JS · 200", ckAnswer: "Direct-answer block present",
    ckSchema: "Schema", ckFail: "not detected",
  },
};

type Str = (typeof T)["es"];

export default function Dashboard({ lang, token }: { lang: Lang; token: string }) {
  const t = T[lang];
  const { selectedSite } = useSites();
  const m = useMetrics(token, selectedSite?.id ?? null);

  if (!selectedSite) {
    return (
      <section className="view on">
        <div className="h-row"><h1>{t.title}</h1></div>
        <p className="sub-line">{t.noSite}</p>
      </section>
    );
  }

  const d = m.data;

  return (
    <section className="view on">
      <div className="h-row">
        <h1>{t.title}</h1>
        <span className="sub">{selectedSite.domain}</span>
      </div>
      {d && d.hasRuns ? (
        <p className="sub-line">
          <b>{d.totals.runs}</b> {t.subRuns} · <b>{d.totals.queries}</b> {t.subQ} ·{" "}
          <b>{d.totals.personas}</b> {t.subP}
          {d.latestWeek && <> · {t.week}: <b>{d.latestWeek}</b></>}
        </p>
      ) : (
        <p className="sub-line" />
      )}

      {m.error && <p className="auth-err">{t.runErr}</p>}

      {m.loading ? (
        <div className="card"><div className="empty"><div className="spinner" /></div></div>
      ) : d && d.hasRuns ? (
        <Loaded t={t} d={d} onRun={m.runNow} running={m.running} progress={m.progress} />
      ) : m.onboarding && !m.running ? (
        <Onboarding t={t} />
      ) : (
        <Empty t={t} onRun={m.runNow} running={m.running} progress={m.progress} />
      )}

      <Recs t={t} token={token} siteId={selectedSite.id} />
    </section>
  );
}

// M6 — the suggestions ledger and the "Cambios verificados" panel share the
// verifications hook so that verifying a rec refreshes both.
function Recs({ t, token, siteId }: { t: Str; token: string; siteId: string }) {
  const v = useVerifications(token, siteId);
  return (
    <>
      <Ledger t={t} token={token} siteId={siteId} onVerified={v.refresh} />
      <VerifiedPanel t={t} v={v} />
    </>
  );
}

// M5 — "Sugerencias de la semana": the weekly recommendation ledger, read from
// real recommendations. Accept → pending (awaiting MCP verification), Reject →
// rejected (app-only). Each card downloads its paste-ready file bundle.
function Ledger({
  t, token, siteId, onVerified,
}: { t: Str; token: string; siteId: string; onVerified: () => void }) {
  const r = useRecommendations(token, siteId);

  const onVerify = async (id: string) => {
    await r.verify(id);
    // Refresh the verified-changes panel whether it passed or failed — the
    // check rows are written either way.
    onVerified();
  };

  return (
    <div className="card mb" style={{ marginTop: 16 }}>
      <div className="card-h">
        <h3>{t.sugT}</h3>
        <span className="sub">{t.sugS}</span>
        <span className="r">
          {r.generating ? (
            <span className="measuring">
              <span className="spinner" style={{ width: 16, height: 16, margin: 0 }} /> {t.genLoad}
            </span>
          ) : (
            <button className="btn btn-ghost" onClick={r.generate}>{t.genBtn}</button>
          )}
        </span>
      </div>

      {r.error && <p className="auth-err" style={{ margin: "0 16px 12px" }}>{t.recsErr}</p>}

      {r.loading ? (
        <div className="empty"><div className="spinner" /></div>
      ) : r.recs.length === 0 ? (
        <div className="empty"><p>{t.emptyRecs}</p></div>
      ) : (
        <>
          {r.recs.map((rec) => (
            <SugCard
              key={rec.id}
              t={t}
              rec={rec}
              onStatus={r.setStatus}
              onVerify={onVerify}
              verifying={r.verifyingId === rec.id}
            />
          ))}
          <p className="fn">{t.fnote}</p>
        </>
      )}
    </div>
  );
}

function impClass(impact: string | null): { cls: string; key: "impHi" | "impMd" | "impLo" } {
  if (impact === "high") return { cls: "hi", key: "impHi" };
  if (impact === "low") return { cls: "lo", key: "impLo" };
  return { cls: "md", key: "impMd" };
}

function SugCard({
  t, rec, onStatus, onVerify, verifying,
}: {
  t: Str;
  rec: Recommendation;
  onStatus: (id: string, s: RecStatus) => void;
  onVerify: (id: string) => void;
  verifying: boolean;
}) {
  const imp = impClass(rec.impact);
  const acted = rec.status !== "new" && rec.status !== "accepted";
  const fileCount = Array.isArray(rec.files) ? rec.files.length : 0;

  return (
    <div className="sug" data-state={rec.status}>
      <h4>{rec.title}</h4>
      {rec.body && <p className="sug-body">{rec.body}</p>}
      <div className="meta">
        <span className={`imp ${imp.cls}`}>{t[imp.key]}</span>
        {rec.kind && <span className="tag">{rec.kind}</span>}
      </div>

      {rec.status === "pending" && (
        <div className="st pend"><span className="d" />{t.stPend}</div>
      )}
      {rec.status === "verified" && (
        <div className="st done"><span className="d" />{t.stDone}</div>
      )}
      {rec.status === "rejected" && (
        <div className="st rej"><span className="d" />{t.stRej}</div>
      )}

      <div className="acts">
        {!acted && (
          <>
            <button className="btn btn-primary act-accept" onClick={() => onStatus(rec.id, "pending")}>
              {t.accept}
            </button>
            <button className="btn btn-ghost act-reject" onClick={() => onStatus(rec.id, "rejected")}>
              {t.reject}
            </button>
          </>
        )}
        {rec.status === "pending" && (
          verifying ? (
            <span className="measuring">
              <span className="spinner" style={{ width: 16, height: 16, margin: 0 }} /> {t.verifying}
            </span>
          ) : (
            <button className="btn btn-primary act-verify" onClick={() => onVerify(rec.id)}>
              ✓ {t.verify}
            </button>
          )
        )}
        {fileCount > 0 && (
          <button className="btn btn-ghost" onClick={() => downloadBundle(rec)}>
            ⬇ {t.download} <span className="mono" style={{ opacity: 0.6 }}>({fileCount} {t.files})</span>
          </button>
        )}
      </div>
    </div>
  );
}

// M6 — "Cambios verificados": the real verification check rows (page renders
// no-JS, expected JSON-LD type present, direct-answer signal present) with an
// ok flag + timestamp. Green ✓ for passed checks (mockup styling), muted ✗ for
// failed ones so the owner sees exactly what still needs publishing.
function checkLabel(t: Str, name: string): string {
  if (name === "page_renders_no_js") return t.ckPage;
  if (name === "direct_answer_present") return t.ckAnswer;
  if (name.startsWith("schema_")) return `${t.ckSchema} ${name.slice("schema_".length)}`;
  return name;
}

function fmtWhen(iso: string, lang: Lang): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(lang === "es" ? "es-MX" : "en-US", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function VerifiedPanel({
  t, v,
}: {
  t: Str;
  v: { verifs: Verification[]; loading: boolean; error: string | null };
}) {
  const lang: Lang = t === T.es ? "es" : "en";
  return (
    <div className="card mb" style={{ marginTop: 16 }}>
      <div className="card-h">
        <h3>{t.verT}</h3>
        <span className="sub">{t.verS}</span>
      </div>
      {v.error && <p className="auth-err" style={{ margin: "0 16px 12px" }}>{t.verErr}</p>}
      {v.loading ? (
        <div className="empty"><div className="spinner" /></div>
      ) : v.verifs.length === 0 ? (
        <div className="empty"><p>{t.verEmpty}</p></div>
      ) : (
        <div className="vlist">
          {v.verifs.map((c) => (
            <div className="v" key={c.id}>
              <span className={`ck ${c.ok ? "" : "no"}`}>{c.ok ? "✓" : "✗"}</span>
              <div>
                <span>
                  {checkLabel(t, c.check_name)}
                  {!c.ok && <span className="mono" style={{ opacity: 0.7 }}> · {t.ckFail}</span>}
                </span>
                <div className="m">{fmtWhen(c.created_at, lang)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// "Midiendo… (done/total)" while the queue drains; falls back to the plain
// measuring copy before the first status poll returns a total.
function progressSuffix(progress: RunProgress | null): string {
  return progress && progress.total > 0 ? ` (${progress.done}/${progress.total})` : "";
}

// M13 — auto-onboarding state: shown while the site sets itself up (reads the
// homepage, seeds the first persona + queries) and its first run drains. Replaces
// the empty "Medir ahora" state; the metrics hook flips to the normal dashboard
// the moment the first run's rows land.
function Onboarding({ t }: { t: Str }) {
  return (
    <div className="card">
      <div className="empty">
        <span className="measuring">
          <span className="spinner" style={{ width: 18, height: 18, margin: 0 }} /> {t.onbT}
        </span>
        <p style={{ marginTop: 12 }}>{t.onbP}</p>
      </div>
    </div>
  );
}

function Empty({
  t, onRun, running, progress,
}: { t: Str; onRun: () => void; running: boolean; progress: RunProgress | null }) {
  return (
    <div className="card">
      <div className="empty">
        <h3>{t.emptyT}</h3>
        <p>{t.emptyP}</p>
        {running ? (
          <span className="measuring"><span className="spinner" style={{ width: 18, height: 18, margin: 0 }} /> {t.measuring}{progressSuffix(progress)}</span>
        ) : (
          <button className="btn btn-primary" onClick={onRun}>✨ {t.measure}</button>
        )}
      </div>
    </div>
  );
}

function fmtDelta(v: number | null, invert = false): { cls: string; text: string } | null {
  if (v == null || v === 0) return v === 0 ? { cls: "flat", text: "0" } : null;
  // invert=true → lower is better (rank): a decrease is good.
  const good = invert ? v < 0 : v > 0;
  const arrow = v > 0 ? "↑" : "↓";
  const mag = Math.abs(v);
  return { cls: good ? "" : "down", text: `${arrow} ${mag}` };
}

function Delta({ v, invert, unit }: { v: number | null; invert?: boolean; unit?: string }) {
  const dd = fmtDelta(v, invert);
  if (!dd) return null;
  return <span className={`delta ${dd.cls}`}>{dd.text}{unit ?? ""}</span>;
}

function Loaded({
  t, d, onRun, running, progress,
}: { t: Str; d: Metrics; onRun: () => void; running: boolean; progress: RunProgress | null }) {
  const k = d.kpis;
  return (
    <>
      <div className="kpis">
        <div className="kpi">
          <div className="lab">{t.k1}</div>
          <div className="val tnum">{k.pctRecommended ?? "—"}<small>%</small></div>
          <div className="foot"><Delta v={k.pctRecommendedDelta} unit=" pts" /><span className="note">{t.k1n}</span></div>
        </div>
        <div className="kpi">
          <div className="lab">{t.k2}</div>
          <div className="val tnum">{k.rankWhenFound ?? "—"}</div>
          <div className="foot"><Delta v={k.rankWhenFoundDelta} invert /><span className="note">{t.k2n}</span></div>
        </div>
        <div className="kpi">
          <div className="lab">{t.k3}</div>
          <div className="val tnum">{k.pctFirstTry ?? "—"}<small>%</small></div>
          <div className="foot"><span className="note">{t.k3n}</span></div>
        </div>
        <div className="kpi">
          <div className="lab">{t.k4}</div>
          <div className="val tnum">{k.shareOfVoice ?? "—"}<small>%</small></div>
          <div className="foot"><span className="note">{t.k4n}</span></div>
        </div>
      </div>

      <div className="card mb" style={{ marginTop: 16 }}>
        <div className="card-h">
          <h3>{t.chT}</h3>
          <span className="sub">{t.chS}</span>
          <span className="r">
            {running ? (
              <span className="measuring"><span className="spinner" style={{ width: 16, height: 16, margin: 0 }} /> {t.measuring}{progressSuffix(progress)}</span>
            ) : (
              <button className="btn btn-ghost" onClick={onRun}>{t.measure}</button>
            )}
          </span>
        </div>
        <div className="chart-wrap"><TrendChart trend={d.trend} /></div>
        <div className="legend">
          <span className="it"><span className="mkdot" /> {t.chT}</span>
        </div>
      </div>

      <div className="card mb">
        <div className="card-h"><h3>{t.aiT}</h3><span className="sub">{t.week}: {d.latestWeek}</span></div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr><th>{t.thP}</th><th>{t.thQ}</th><th>{t.aiT}</th><th>{t.thTry}</th><th>{t.thR}</th><th>{t.thCite}</th></tr>
            </thead>
            <tbody>
              {d.engines.flatMap((g) =>
                g.rows.map((r, i) => (
                  <EngineTableRow
                    key={`${g.engine}-${i}`}
                    t={t}
                    row={r}
                    engine={g.label}
                    search={d.searchByEngine[g.engine]}
                  />
                )),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function EngineTableRow({
  t, row, engine, search,
}: { t: Str; row: EngineRow; engine: string; search: boolean | undefined }) {
  const tryLabel = row.included && row.tryIndex != null ? String(row.tryIndex + 1) : t.miss;
  const tryCls = !row.included ? "b-crit" : row.tryIndex === 0 ? "b-good" : "b-warn";
  return (
    <tr>
      <td>{row.persona}</td>
      <td><span className="q">{row.query}</span></td>
      <td className="mono">
        {engine}
        <span className={`eng-tag ${search ? "search" : "base"}`}>{search ? t.search : t.base}</span>
      </td>
      <td><span className={`badge ${tryCls}`}>{tryLabel}</span></td>
      <td className="tnum">{row.listRank != null ? `${row.listRank}º` : "—"}</td>
      <td>
        {row.citedWithLink ? (
          <span className="cite link">🔗 {t.link}</span>
        ) : row.included ? (
          <span className="cite">• {t.ment}</span>
        ) : (
          <span className="cite no">— {t.none}</span>
        )}
      </td>
    </tr>
  );
}

// Trend of % recommended over weeks (up = better). SVG, theme-token colours.
function TrendChart({ trend }: { trend: { week: string; pct: number }[] }) {
  const W = 720, H = 240, padL = 40, padR = 18, padT = 16, padB = 34;
  const n = trend.length;
  const x = (i: number) => (n <= 1 ? padL + (W - padL - padR) / 2 : padL + (W - padL - padR) * (i / (n - 1)));
  const y = (v: number) => padT + (H - padT - padB) * ((100 - v) / 100);
  const pts = trend.map((p, i) => `${x(i)},${y(p.pct)}`).join(" ");
  const gridVals = [0, 25, 50, 75, 100];

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="% recommended trend">
      <g className="grid">
        {gridVals.map((v) => (
          <g key={v}>
            <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} />
            <text className="axis" x={padL - 8} y={y(v) + 4} textAnchor="end">{v}%</text>
          </g>
        ))}
      </g>
      {n >= 1 && (
        <polygon className="area" points={`${pts} ${x(n - 1)},${y(0)} ${x(0)},${y(0)}`} />
      )}
      {n >= 2 && <polyline className="ln" points={pts} />}
      {trend.map((p, i) => (
        <circle key={i} className="dot" cx={x(i)} cy={y(p.pct)} r={4.5} />
      ))}
      {trend.map((p, i) =>
        i % 2 === 0 || i === n - 1 ? (
          <text key={`x${i}`} className="axis" x={x(i)} y={H - 14} textAnchor="middle">{p.week.slice(5)}</text>
        ) : null,
      )}
    </svg>
  );
}
